import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { map, timer } from 'rxjs';

/** Seconds of loading before the elapsed time appears. */
export const SEARCH_ELAPSED_DELAY_SECONDS = 3;

/**
 * The elapsed time of a slow search, "Still searching … 12 s". A search is a
 * single SQL query without a percentage, so the elapsed time is the progress
 * information. The component counts from its creation: it lives inside the
 * loading state and disappears with it. The text appears after
 * SEARCH_ELAPSED_DELAY_SECONDS and updates every second.
 *
 * Screen readers hear "Still searching" once when it appears: the live region
 * is present from the start and gets its text once, the seconds beside it are
 * hidden from assistive technology.
 */
@Component({
  selector: 'app-search-elapsed',
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.visible]': 'visible()' },
  template: `<span class="search-elapsed-label" aria-live="polite">{{
      visible() ? ('common.label.stillSearching' | translate) : ''
    }}</span>
    @if (visible()) {
      <span class="search-elapsed-seconds" aria-hidden="true">{{
        'common.label.elapsedSeconds' | translate: { seconds: seconds() }
      }}</span>
    }`,
  styleUrls: ['./search-elapsed.component.scss'],
})
export class SearchElapsedComponent {
  private readonly start = Date.now();

  /** Whole seconds since the loading began, null before the delay is over. */
  public readonly seconds = toSignal(
    timer(SEARCH_ELAPSED_DELAY_SECONDS * 1000, 1000).pipe(
      // Measured, not counted: a throttled background tab skips ticks.
      map(() => Math.floor((Date.now() - this.start) / 1000))
    ),
    { initialValue: null }
  );

  public readonly visible = computed(() => this.seconds() !== null);
}
